import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import {
  CheckCircle as CheckCircleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutlineOutlined as ErrorOutlineOutlinedIcon,
  GroupOutlined as GroupOutlinedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  TimelapseOutlined as TimelapseOutlinedIcon,
} from '@mui/icons-material';
import { Box, Button, Paper, Typography, Stack } from '@mui/material';
import { 
  PageLayout, 
  MainContent, 
  ContentContainer,
  PageTitle,
  PageSubtitle,
} from '../../styles/SharedStyles';
import ExpertSidebar from '../../components/expert/ExpertSidebar';
import styled from 'styled-components';
import PricingSection from '../../components/PricingSection';
import UserQuantityDialog from '../../components/UserQuantityDialog';
import { AppDispatch } from '../../store'
import {
  fetchSubscription,
  createStripePortalSession,
  changeSubscriptionProducts,
  changeSubscriptionQuantity,
} from '../../redux/slices/subscriptions/subscriptionsSlice';
import {
  createStripeCheckoutSession,
} from '../../redux/slices/checkout/checkoutSlice';
import { useSubscriptions } from '../../redux/slices/subscriptions/useSubscriptions';
import { useAccessToken } from '../../redux/slices/accessToken/useAccessToken';
import { useUser } from '../../redux/slices/user/useUser';
import { createExpert } from '../../redux/slices/expert/expertSlice';
import {
  getExpertProfile,
  SubscriptionInterface,
} from '../../utils/triplinkService';
import { productFree, productIndividual, productBusiness, productUpgradeOptions, productDowngradeOptions } from '../../data/subscription';

type ChoosePlanContentProps = {
  onSubscribe: (prices: string[]) => Promise<void>;
  shouldTrial: boolean;
}

const ChoosePlanContent: React.FC<ChoosePlanContentProps> = (props) => {
  return (
    <>
      <Typography variant="subtitle1">Choose the plan that best fits your needs. All plans include a free trial.</Typography>
      <PricingSection
        onSelectPrices={props.onSubscribe}
        shouldTrial={props.shouldTrial}
        defaultInterval={'month'}
        defaultQuantity={1}
        skipQuantity={false}
        canChangeInterval={true}
        enabledProducts={['free', 'prod_tl_individual-2025-wGCk4x2O6p3j', 'prod_tl_business-2025-N9kH4gDqXydb']}
      />
    </>
  )
}

type PromptPaymentDetailsProps = {
  onManageStripe: () => Promise<void> | undefined;
}
const PromptPaymentDetails: React.FC<PromptPaymentDetailsProps> = (props) => {
  return (
    <>
      <Typography variant="subtitle1">Add payment details to continue using your subscription.</Typography>
      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        onClick={() => props?.onManageStripe()}
      >
        Add Payment Details
      </Button>
    </>
  )
}

type ChangePlanContentProps = {
  onChangePlan: (prices: string[], quantity: number) => Promise<void>;
  onCancel: () => void;
  enabledProducts: string[];
  interval: 'month' | 'year';
  quantity: number;
}

const ChangePlanContent: React.FC<ChangePlanContentProps> = (props) => {
  return (
    <>
      <Typography variant="h6">Change plan, choose the plan that best fits your needs.</Typography>
      <PricingSection
        onSelectPrices={props.onChangePlan}
        shouldTrial={false}
        canChangeInterval={false}
        defaultInterval={props.interval}
        defaultQuantity={props.quantity}
        skipQuantity={false}
        enabledProducts={props.enabledProducts}
      />
      <Button
        variant="outlined"
        color="primary"
        sx={{ mt: 2 }}
        onClick={props.onCancel}
      >
        Cancel
      </Button>
    </>
  )
}

type SubscriptionDetailsProps = {
  subscription: SubscriptionInterface | null;
  canManageStripe: boolean;
  onManageStripe: () => Promise<void> | undefined;
  onChooseProductChange: () => void | undefined;
  onChooseUserQuantity: () => void | undefined;
}
const SubscriptionDetails: React.FC<SubscriptionDetailsProps> = (props) => {
  const {subscription} = props;
  if (!subscription) {
    return null;
  }
  const startDateText = moment.utc(subscription.start_date).format('YYYY-MM-DD');
  let endDateText = '';
  if (subscription.end_date) {
    endDateText = moment.utc(subscription.end_date).format('YYYY-MM-DD');
  }
  const baseProductId = subscription?.items?.[0]?.product_id ?? '';//FIXME search for the correct subscription item.
  const baseProduct = (baseProductId == productFree.id && productFree)
    || (baseProductId == productIndividual.id && productIndividual)
    || (baseProductId == productBusiness.id && productBusiness)
    || null;
  const productDowngrades = productDowngradeOptions[baseProductId]?.baseProducts ?? [];
  const productUpgrades = productUpgradeOptions[baseProductId]?.baseProducts ?? [];
  const productsText = subscription?.items?.map((i) => (i.product?.name ?? '?')).join(', ')
  const numberOfUsers = subscription?.items?.[0]?.product_id == baseProductId && subscription?.items?.[0]?.quantity;
  const canChangeBaseProduct = productDowngrades.length > 0 || productUpgrades.length > 0;
  const canChangeUserQuantity = baseProduct?.requiresQuantity ?? false;
  const isStatusGood = ['active','trialing'].includes(subscription.status);
  return (
    <Paper elevation={1} square={false} style={{padding: "6px"}}>
      <Typography variant="h6">{subscription.plan_name} Plan</Typography>
      <Stack direction="row" spacing={1}>
        <TimelapseOutlinedIcon/>
        <Typography variant="body1">{startDateText} to {endDateText}</Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        {isStatusGood && <CheckCircleOutlineIcon/>}
        {!isStatusGood && <ErrorOutlineOutlinedIcon/>}
        <Typography variant="body1">Status: {subscription.status}</Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        <Inventory2OutlinedIcon/>
        <Typography variant="body1">Products: {productsText}</Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        <GroupOutlinedIcon/>
        <Typography variant="body1">Number of users: {numberOfUsers}</Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        {props.canManageStripe && (
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => props?.onManageStripe()}
          >
            Manage Billing
          </Button>
        )}
        {canChangeUserQuantity && (
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => props?.onChooseUserQuantity()}
          >
            Change user amount
          </Button>
        )}
        {canChangeBaseProduct && (
          <Button
            variant="outlined"
            color="primary"
            sx={{ mt: 2 }}
            onClick={() => props?.onChooseProductChange()}
          >
            Change plan
          </Button>
        )}
      </Stack>
    </Paper>
  )
}

const BillingPage: React.FC = () => {
  const { body: accessToken } = useAccessToken()
  const dispatch = useDispatch<AppDispatch>();
  const {subscription, updatingSubscription} = useSubscriptions();
  const user = useUser();
  const currentUser = user.body;
  const [isChangingPlan, setIsChangingPlan] = useState<boolean>(false)
  const [isChangingQuantity, setIsChangingQuantity] = useState<boolean>(false)

  useEffect(() => {
    dispatch(fetchSubscription()).catch(error => {console.log(error);})
  }, [])

  const shouldTrial = currentUser?.has_trialed === false;

  const handleSubscribe = async (prices: string[]) => {
    // Create expert profile without Stripe checkout on the free plan
    if (accessToken && prices[0] === 'free') {
      try {
        const expert = await getExpertProfile(accessToken);
        
        alert(`Already subscribed to the ${prices[0]} plan`)
        return expert
      } catch (error) {
        dispatch(createExpert({
          requiresAccessToken: true
        }));
      }
    } else {
      try {
        const result = await dispatch(createStripeCheckoutSession({ price_keys: prices, qty: 1, trial: shouldTrial })).unwrap()
        // Navigate to Stripe checkout.
        window.location.replace(result.url)
        return result;
      } catch(error) {
        console.log(error);
      }
    }
  };

  const canManageStripe: boolean = (
    !!subscription && subscription.billing_user_id == currentUser?.id
  ) ?? false;
  const shouldPromptPaymentDetails = canManageStripe
    && (subscription?.missing_payment_method ?? false);
  const onManageStripe = async () => {
    try {
      const pSession = await dispatch(createStripePortalSession({})).unwrap();
      // Navigate to Stripe management portal.
      window.location.replace(pSession.url)
    } catch(error) {
      console.log(error);
    }
  }

  const baseProductId = subscription?.items?.[0]?.product_id ?? '';//FIXME search for the correct subscription item.
  const baseProduct = (baseProductId == productFree.id && productFree)
    || (baseProductId == productIndividual.id && productIndividual)
    || (baseProductId == productBusiness.id && productBusiness)
    || null;
  const productDowngrades = productDowngradeOptions[baseProductId]?.baseProducts ?? [];
  const productUpgrades = productUpgradeOptions[baseProductId]?.baseProducts ?? [];
  const productsText = subscription?.items?.map((i) => (i.product?.name ?? '?')).join(', ')
  const numberOfUsers = subscription?.items?.[0]?.product_id == baseProductId && subscription?.items?.[0]?.quantity || null;
  const canChangeBaseProduct = productDowngrades.length > 0 || productUpgrades.length > 0;
  const canChangeUserQuantity = baseProduct?.requiresQuantity ?? false;
  const existingBillingInterval = subscription?.items?.[0]?.price?.interval ?? 'month';//FIXME temporary typescript workaround. This should not really have a default value, it must match the subscription.
  const existingBillingPrice = subscription?.price_total ?? undefined;
  const combined_unit_price = subscription?.items?.reduce(
    (accum, val) => (accum + Number.parseFloat(val.price?.amount ?? '0')),
    0
  ) ?? undefined;

  const handleChangePlan = async (prices: string[], quantity: number) => {
    if (prices[0] === 'free') {
      // Downgrade by cancelling current subscription.
      // TODO implement a flow="subscription_cancel" Stripe portal session and navigate the user to it.
      alert('Please cancel your current subscription to default to the free plan');
    } else if (baseProductId === 'prod_tl_individual-2025-wGCk4x2O6p3j' && prices[0].startsWith('tl_business-')) {
      // Upgrade from an individual to a business plan.
      try {
        if (!window.confirm(`Change to the business plan with ${quantity} users? This will update your subscription costs.`)) {
          return;
        }
        const result = await dispatch(changeSubscriptionProducts({
          price_keys: prices, quantity: quantity,
        })).unwrap()
        setIsChangingPlan(false);
      } catch(error) {
        console.log(error);
      }
    } else if (baseProductId === 'prod_tl_business-2025-N9kH4gDqXydb' && prices[0].startsWith('tl_individual-')) {
      alert('Please contact Triplink support to arrange downgrading from a business plan.');
    }
  };

  const handleChangeQuantity = async (quantity: number) => {
    try {
      setIsChangingQuantity(false);
      //FIXME implement a better confirmation dialog with preview pricing.
      if (!window.confirm(`Change subscription to ${quantity} users? This will update your subscription costs.`)) {
        return;
      }
      const result = await dispatch(changeSubscriptionQuantity({
        quantity: quantity,
      })).unwrap()
      return result;
    } catch(error) {
      console.log(error);
    }
  };

  const isDetailVisible: boolean = !!subscription && !isChangingPlan;

  return (
    <PageLayout>
      <ExpertSidebar />
      <MainContent>
        <ContentContainer>
          <PageTitle variant="h4" fontWeight={600} gutterBottom>Billing & Subscription</PageTitle>
          {!subscription && (
            <ChoosePlanContent onSubscribe={handleSubscribe} shouldTrial={shouldTrial}/>
          )}
          {shouldPromptPaymentDetails && (
            <PromptPaymentDetails onManageStripe={onManageStripe}/>
          )}
          {isChangingPlan && (
            <ChangePlanContent
              onChangePlan={handleChangePlan}
              onCancel={() => {setIsChangingPlan(false)}}
              enabledProducts={productUpgrades.map(x => x.id).concat(productDowngrades.map(x => x.id))}
              interval={existingBillingInterval}
              quantity={numberOfUsers ?? 1}
            />
          )}
          {isChangingQuantity && (
            <UserQuantityDialog
              onConfirm={handleChangeQuantity}
              onCancel={() => {setIsChangingQuantity(false)}}
              defaultQuantity={numberOfUsers ?? 1}
              unitPrice={combined_unit_price}
              interval={existingBillingInterval}
              visible={isChangingQuantity}
            />
          )}
          {isDetailVisible && (
            <SubscriptionDetails
              subscription={subscription}
              canManageStripe={canManageStripe}
              onManageStripe={onManageStripe}
              onChooseProductChange={() => {setIsChangingPlan(true)}}
              onChooseUserQuantity={() => {setIsChangingQuantity(true)}}
            />
          )}
        </ContentContainer>
      </MainContent>
    </PageLayout>
  );
};

const StyledCheckIcon = styled(CheckCircleIcon)`
  color: #1976d2;
`;

export default BillingPage;
